<template>
  <div :id="`${source}_audit_trail_renderer`">
    <div
      v-for="(item, index) in processedAudits"
      :key="`audit_${index}`"
      class="flex flex-col gap-3 p-4 rounded-[10px] border_8 bg_2 mb-2"
    >
      <div class="flex flex-row gap-2 items-center justify-between truncate">
        <div class="flex flex-row gap-2 items-center truncate">
          <ProfileIcon :size="32" :font="14" :data="item.actionByData" />
          <div
            v-if="item.type === 'combined' && item.entityDetails?.length >= 1"
          >
            <span v-html="getMessageBeforeCount(item.message)"></span>
            <CustomDropdown
              maxHeight="300px"
              gap="10px"
              position="center"
              dropdownCSS="px-3 py-2"
              :on-hover="true"
              :relative-to-viewport="true"
            >
              <template #trigger>
                <span
                  class="hover:underline cursor-pointer"
                  v-html="getCountPortion(item.message)"
                ></span>
              </template>

              <div class="flex flex-col">
                <div
                  v-for="(detail, detailIndex) in item.entityDetails"
                  :key="detailIndex"
                  class="flex flex-row items-center gap-2 py-2 px-3"
                >
                  <ProfileIcon :size="28" :font="12" :data="detail" />
                  <div class="flex flex-col gap-1 font-normal truncate">
                    <span class="text-sm primary_color truncate">{{
                      detail.name
                    }}</span>
                    <span class="text-xs secondary_color truncate">{{
                      detail.email
                    }}</span>
                  </div>
                </div>
              </div>
            </CustomDropdown>
            <span v-html="getMessageAfterCount(item.message)"></span>
          </div>
          <span class="truncate" v-else v-html="item.message"></span>
        </div>

        <div class="text-xs font-normal secondary_color text-nowrap">
          {{
            formatTimestamp(item.timestamp, "timestamp", formatTypes.centered)
          }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { renderEntity } from "@/src/utils/utils.js";
import {
  generateAuditMessage,
  generateCombinedMessage,
  getEntityDetails,
} from "@/src/utils/auditUtils.js";
import { formatTimestamp } from "@/src/utils/dateTimeUtils";
import { formatTypes } from "@/src/constants/dateTimeConstants.js";
import {
  auditEntityTypes,
  auditOperationType,
  groupableAuditCriteria,
  timeThreshold,
} from "@/src/constants/auditConstants.js";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import CustomDropdown from "@/src/components/CustomDropdown.vue";

const props = defineProps({
  source: { type: String, required: true },
  audits: { type: Array, required: true },
  sequenceName: { type: String, default: null },
  isLastPage: { type: Boolean, default: false },
});

const processedAudits = computed(() => {
  return processAudits(props.audits);
});

const getMessageBeforeCount = (message) => {
  try {
    const regex = /<b>(\d+) (contacts\/leads|contact\/lead)<\/b>/;
    const match = message.match(regex);
    if (match) {
      return message.substring(0, message.indexOf(match[0]));
    }
    return "";
  } catch (error) {
    console.error("Error getting message before count:", error);
    return "";
  }
};

const getCountPortion = (message) => {
  try {
    const regex = /<b>(\d+) (contacts\/leads|contact\/lead)<\/b>/;
    const match = message.match(regex);
    if (match) {
      return match[0];
    }
    return "";
  } catch (error) {
    console.error("Error getting count portion:", error);
    return "";
  }
};

const getMessageAfterCount = (message) => {
  try {
    const regex = /<b>(\d+) (contacts\/leads|contact\/lead)<\/b>/;
    const match = message.match(regex);
    if (match) {
      return message.substring(message.indexOf(match[0]) + match[0].length);
    }
    return "";
  } catch (error) {
    console.error("Error getting message after count:", error);
    return "";
  }
};

const processAudits = (auditList) => {
  try {
    if (!auditList || auditList.length === 0) return auditList;

    let filteredAudits = filterSequenceSettingInserts(auditList);
    filteredAudits = filterSequenceStepMessageAudits(filteredAudits);

    const processed = [];
    let currentGroup = null;

    for (let i = 0; i < filteredAudits.length; i++) {
      const audit = filteredAudits[i];

      if (shouldGroupAudit(audit)) {
        if (currentGroup && canCombineWithGroup(audit, currentGroup)) {
          currentGroup.count += 1;
          currentGroup.audits.push(audit);
          continue;
        } else {
          if (currentGroup) {
            const combinedItem = createCombinedAuditItem(currentGroup);
            if (combinedItem) {
              processed.push(combinedItem);
            }
          }

          const entityType =
            audit.entity_type === auditEntityTypes.SEQUENCE_EXECUTION
              ? audit.entity_details?.entity_type
              : audit.after?.entity_type;

          const operationType =
            audit.entity_type === auditEntityTypes.SEQUENCE_EXECUTION &&
            (audit.before?.status === "PENDING" ||
              audit.before?.status === "IN_PROGRESS") &&
            audit.after?.status === "REMOVED"
              ? auditOperationType.DELETE
              : audit.operation_type;

          currentGroup = {
            actionBy: audit.action_by,
            entityType: entityType,
            operationType: operationType,
            timestamp: audit.timestamp,
            count: 1,
            audits: [audit],
          };
        }
      } else {
        if (currentGroup) {
          const combinedItem = createCombinedAuditItem(currentGroup);
          if (combinedItem) {
            processed.push(combinedItem);
          }
          currentGroup = null;
        }

        if (audit.entity_type === auditEntityTypes.SEQUENCE_SETTING) {
          const settingMessages =
            generateSequenceSettingIndividualMessages(audit);
          if (settingMessages && settingMessages.length > 0) {
            processed.push(...settingMessages);
          }
        } else if (audit.entity_type === auditEntityTypes.SEQUENCE_STEP) {
          const stepMessages = generateSequenceStepIndividualMessages(audit);
          if (stepMessages && stepMessages.length > 0) {
            processed.push(...stepMessages);
          }
        } else {
          const singleItem = createSingleAuditItem(audit);
          if (singleItem) {
            processed.push(singleItem);
          }
        }
      }
    }

    if (currentGroup) {
      const combinedItem = createCombinedAuditItem(currentGroup);
      if (combinedItem) {
        processed.push(combinedItem);
      }
    }

    return processed.filter((item) => item !== null && item !== undefined);
  } catch (error) {
    console.error("An error occurred while processing audits: ", error);
    return [];
  }
};

const filterSequenceStepMessageAudits = (auditList) => {
  try {
    const stepMessageInserts = auditList.filter(
      (audit) =>
        audit.entity_type === auditEntityTypes.SEQUENCE_STEP_MESSAGES &&
        audit.operation_type === "insert",
    );

    if (stepMessageInserts.length === 0) return auditList;

    return auditList.filter((audit) => {
      if (
        audit.entity_type === auditEntityTypes.SEQUENCE_STEP_MESSAGES &&
        audit.operation_type === "update"
      ) {
        const matchingStepMessageInsert = stepMessageInserts.find(
          (insert) => insert.entity_id === audit.entity_id,
        );

        if (matchingStepMessageInsert) {
          const timeDiff = Math.abs(
            new Date(matchingStepMessageInsert.timestamp) -
              new Date(audit.timestamp),
          );
          const thirtySeconds = 30 * 1000;

          const isImmediateUpdate = timeDiff <= thirtySeconds;
          const hasOnlyActivationChange =
            audit.before &&
            audit.after &&
            Object.keys(audit.before).length === 2 &&
            Object.keys(audit.after).length === 2 &&
            audit.before.hasOwnProperty("is_active") &&
            audit.before.hasOwnProperty("updated_at") &&
            audit.after.hasOwnProperty("is_active") &&
            audit.after.hasOwnProperty("updated_at") &&
            audit.before.is_active !== audit.after.is_active;

          if (isImmediateUpdate && hasOnlyActivationChange) {
            return false;
          }
        }
      }
      return true;
    });
  } catch (error) {
    console.error(
      "An error occurred while filtering sequence step message audits: ",
      error,
    );
    return auditList;
  }
};

const filterSequenceSettingInserts = (auditList) => {
  try {
    const sequenceInserts = auditList.filter(
      (audit) =>
        audit.entity_type === auditEntityTypes.SEQUENCE &&
        audit.operation_type === "insert",
    );

    if (sequenceInserts.length === 0) return auditList;

    const filteredList = auditList.filter((audit) => {
      if (
        audit.entity_type === auditEntityTypes.SEQUENCE_SETTING &&
        audit.operation_type === "insert"
      ) {
        const matchingSequenceInsert = sequenceInserts.find(
          (seq) => seq.entity_id === audit.entity_id,
        );

        if (matchingSequenceInsert && props.isLastPage) {
          const timeDiff = Math.abs(
            new Date(matchingSequenceInsert.timestamp) -
              new Date(audit.timestamp),
          );
          const tenSeconds = 10 * 1000;

          if (timeDiff <= tenSeconds) {
            return false;
          }
        }
      }
      return true;
    });

    return filteredList;
  } catch (error) {
    console.error(
      "An error occurred while filtering sequence setting inserts: ",
      error,
    );
    return auditList;
  }
};

const generateSequenceSettingIndividualMessages = (audit) => {
  try {
    const actionBy = renderEntity(audit?.action_by || "You");
    const messages = generateAuditMessage(actionBy, audit, props.sequenceName);

    if (!messages) return [];

    if (Array.isArray(messages)) {
      return messages.map((message) => ({
        message: message,
        timestamp: audit.timestamp,
        type: "individual_setting",
        actionByData: audit.action_by,
      }));
    } else {
      return [
        {
          message: messages,
          timestamp: audit.timestamp,
          type: "single_setting",
          actionByData: audit.action_by,
        },
      ];
    }
  } catch (error) {
    console.error(
      "An error occurred while generating sequence setting individual messages: ",
      error,
    );
    return [];
  }
};

const generateSequenceStepIndividualMessages = (audit) => {
  try {
    const actionBy = renderEntity(audit?.action_by || "You");
    const messages = generateAuditMessage(actionBy, audit, props.sequenceName);

    if (!messages) return [];

    if (Array.isArray(messages)) {
      return messages.map((message) => ({
        message: message,
        timestamp: audit.timestamp,
        type: "individual_step",
        actionByData: audit.action_by,
      }));
    } else {
      return [
        {
          message: messages,
          timestamp: audit.timestamp,
          type: "single_step",
          actionByData: audit.action_by,
        },
      ];
    }
  } catch (error) {
    console.error(
      "An error occurred while generating sequence step individual messages: ",
      error,
    );
    return [];
  }
};

const shouldGroupAudit = (audit) => {
  try {
    if (
      audit.entity_type === auditEntityTypes.SEQUENCE_EXECUTION &&
      audit.operation_type === auditOperationType.UPDATE
    ) {
      const statusChange =
        (audit.before?.status === "PENDING" ||
          audit.before?.status === "IN_PROGRESS") &&
        audit.after?.status === "REMOVED";
      const hasValidEntityType =
        audit.entity_details?.entity_type &&
        groupableAuditCriteria.targetEntityTypes.includes(
          audit.entity_details.entity_type.toLowerCase(),
        );

      return statusChange && hasValidEntityType;
    }

    return (
      groupableAuditCriteria.entityTypes.includes(audit.entity_type) &&
      groupableAuditCriteria.operationTypes.includes(audit.operation_type) &&
      audit.after?.entity_type &&
      groupableAuditCriteria.targetEntityTypes.includes(
        audit.after.entity_type.toLowerCase(),
      )
    );
  } catch (error) {
    console.error(
      "An error occurred while checking if its a group audit or not: ",
      error,
    );
    return false;
  }
};

const canCombineWithGroup = (audit, group) => {
  try {
    const timeDiff = new Date(group.timestamp) - new Date(audit.timestamp);

    if (
      audit.entity_type === auditEntityTypes.SEQUENCE_EXECUTION &&
      audit.operation_type === auditOperationType.UPDATE
    ) {
      const statusChange =
        (audit.before?.status === "PENDING" ||
          audit.before?.status === "IN_PROGRESS") &&
        audit.after?.status === "REMOVED";
      const sameEntityType =
        audit.entity_details?.entity_type?.toLowerCase() ===
        group.entityType?.toLowerCase();
      const sameAction =
        JSON.stringify(audit.action_by) === JSON.stringify(group.actionBy);
      const sameOperationType =
        group.operationType === auditOperationType.DELETE;

      return (
        Math.abs(timeDiff) <= timeThreshold.FIVE_MINUTES &&
        statusChange &&
        sameEntityType &&
        sameAction &&
        sameOperationType
      );
    }

    return (
      Math.abs(timeDiff) <= timeThreshold.FIVE_MINUTES &&
      groupableAuditCriteria.entityTypes.includes(audit.entity_type) &&
      audit.operation_type === group.operationType &&
      audit.after?.entity_type?.toLowerCase() ===
        group.entityType?.toLowerCase() &&
      JSON.stringify(audit.action_by) === JSON.stringify(group.actionBy)
    );
  } catch (error) {
    console.error(
      "An error occurred while checking if we can combine with group: ",
      error,
    );
    return false;
  }
};

const createCombinedAuditItem = (group) => {
  try {
    const actionBy = renderEntity(group.audits[0]?.action_by || "You");
    const entityDetails = getEntityDetails(group.audits);
    const message = generateCombinedMessage(
      actionBy,
      group.operationType,
      group.entityType,
      group.count,
      props.sequenceName,
      group.audits,
    );

    if (!message) return null;

    return {
      message: message,
      timestamp: group.audits[0].timestamp,
      type: "combined",
      entityDetails: entityDetails,
      actionByData: group.audits[0].action_by,
    };
  } catch (error) {
    console.error(
      "An error occurred while creating combined audit item: ",
      error,
    );
    return null;
  }
};

const createSingleAuditItem = (audit) => {
  try {
    const actionBy = renderEntity(audit?.action_by || "You");
    const message = generateAuditMessage(actionBy, audit, props.sequenceName);

    if (!message) return null;

    return {
      message: Array.isArray(message) ? message.join("<br>") : message,
      timestamp: audit.timestamp,
      type: "single",
      actionByData: audit.action_by,
    };
  } catch (error) {
    console.error(
      "An error occurred while creating single audit item: ",
      error,
    );
    return null;
  }
};
</script>
